import { compose, mdsWidget } from 'dojo/_base/declare';
import lang from 'dojo/_base/lang';
import template from './PerformerCard.html?raw';
import { AddressWizardTypes as Types } from "../../AddressWizardTypes";

import _uiFrameMixin from '../../../../moreCore/UI/Container/uiFrame';
import PageStack from '../../../../moreCore/UI/Tabbing/PageStack';
import HintBox from '../../../../moreCore/UI/HintBox/HintBox';
import CheckBox from '../../../../moreCore/UI/CheckBox/CheckBox';
import EntityStore from '../../../../moreCore/Tools/EntityStore';
import StaticTable from '../../../../moreCore/UI/StaticTable/StaticTable';
import _GlobalSelectEntityDialogsMixin from '../../../_loadingHelpers/_GlobalSelectEntityDialogsMixin';
import { SelectEntityDialogTypes } from "../../../../moreCore/_loadingHelpers/SelectEntityDialog";
import { DateHelpers, EntityHelpers } from "../../../../more/Helpers";
import EnsembleMemberEditor from './EnsembleMemberEditor/EnsembleMemberEditor';

class PerformerCard extends compose(_uiFrameMixin, _GlobalSelectEntityDialogsMixin) {
    templateString: any = template;

    private addEnsembleHintbox: MdsLax<InstanceType<typeof HintBox>>;    // => to Interpret, PageStack page 1
    private addInterpretHintbox: MdsLax<InstanceType<typeof HintBox>>;   // => to Ensemble, PageStack page 2

    private entityId: string;
    private addressType: "person" | "company"
    private isNew: boolean = false;
    private pageStack: MdsLax<InstanceType<typeof PageStack>>;
    private profession: MdsLax<InstanceType<typeof HintBox>>;
    private isMainProfesion: MdsLax<InstanceType<typeof CheckBox>>;

    private memberInStore: MdsLax<InstanceType<typeof EntityStore<Types.EnsembleMember>>>;
    private memberInTable: MdsLax<InstanceType<typeof StaticTable>>;
    private memberInTableContainer: HTMLElement;

    private membersOfStore: MdsLax<InstanceType<typeof EntityStore<Types.EnsembleMember>>>;
    private membersOfTable: MdsLax<InstanceType<typeof StaticTable>>;
    private membersOfTableContainer: HTMLElement;

    constructor(addressType: "person" | "company") {
        super(...(arguments as unknown as []));
        this.addressType = addressType;
    }

    private emitDataChanged() {

        let data = this.getDataState();
        this.emit("DataChanged", data);
    }
    private getDataState() {

        return {
            ID: this.entityId,
            isNew: this.isNew,
            profession: this.profession.getValue("value"),
            isMainProfesion: this.isMainProfesion.getValue(),
            memberInStoreData: this.memberInStore?.getDiffData(),
            membersOfStoreData: this.membersOfStore?.getDiffData()
        }
    }

    private _onValueChange_Profession() {
        this.emitDataChanged();
    }

    private _onValueChange_isMainProfesionBox(value) {

        if (value === true) {
            this.emit("change_mainProfession", this.entityId);
        }
        this.emitDataChanged();
    }

    adjustMainProfession(newMainId: string, evt) {
        if (this.entityId != newMainId && this.isMainProfesion?.getValue() === true) {
            this.isMainProfesion.setValue(false);
        }
    }

    private deletePerformerCard() {
        this.emit("deleted", this.isNew ? null : this.entityId);

        this.destroyRecursive();
    }

    setup(node: HTMLElement, tableLayouts: Array<StaticTable.Layout>, performer?: Types.Performer_Extended, isNew = false) {

        if (isNew) {
            this.isNew = true;
            this.entityId = (Math.random() * 100000).toString();
        }
        if (performer) {
            this.entityId = performer.ID;

            this.profession.setValue(performer.ref_ev_profession, true);
            this.isMainProfesion.setValue(performer.is_main_profession, true);
        }

        let map = tableLayouts.map((l) => { return l.name; });

        switch (this.addressType) {
            case "person":
                this.pageStack.hidePage("membersOf")
                this.setupMemberIn(performer ? performer.EL_ensemble_members_performer : [], tableLayouts[map.indexOf("memberInTable")]);
                break;
            case "company":
                this.pageStack.hidePage("memberIn")
                this.setupMembersOf(performer ? performer.EL_ensemble_members_ensemble : [], tableLayouts[map.indexOf("membersOfTable")]);
                break;
        }

        this.show("node", null, null, node);
        this.createDefaultButton("delete", lang.hitch(this, this.deletePerformerCard), true, "primary");
    }

    private _tableFormatter(cell: StaticTable.StaticTableCell<any>) {
        switch (cell.name) {
            case "date_start":
            case "date_end":
                let date = cell.value ? DateHelpers.dateTimeToString(new Date(cell.value), "dd.MM.yyyy") : "";
                cell.contentnode.innerHTML = date;
                return true;
        }
        return false;
    }

    private setupMemberIn(list: Array<Types.EnsembleMember>, layout: StaticTable.Layout) {

        this.memberInStore = new EntityStore();
        this.memberInStore.setup(list);

        this.memberInTable = new StaticTable();
        this.memberInTable.setLayout(layout);
        this.memberInTable.setFormatter(this._tableFormatter);

        this.memberInTable.placeAt(this.memberInTableContainer);
        this.memberInTable.addDefaultRowActionButton("edit", lang.hitch(this, this.editEntry, "ref_ensemble"));
        this.memberInTable.addDefaultRowActionButton("delete", lang.hitch(this, this.deleteMemberInEntry));
        this.memberInTable.setRowClick(lang.hitch(this, this._onMemberInTableRowClick))

        this.memberInTable.setCyAttribute("PerformerCardsMemberIn");

        this.updateMemberInTable();
    }
    private updateMemberInTable() {

        this.memberInTable.setupFromStore(this.memberInStore);
        this.pageStack.setPageLabel("memberIn", `${this.translate("AddressWizard_Performer_MemberIn")} (${this.memberInStore.getCurrentCompleteCount()})`)
    }

    private setupMembersOf(list: Array<Types.EnsembleMember>, layout: StaticTable.Layout) {

        this.membersOfStore = new EntityStore();
        this.membersOfStore.setup(list);

        this.membersOfTable = new StaticTable();
        this.membersOfTable.setLayout(layout);
        this.membersOfTable.setFormatter(this._tableFormatter);
        this.membersOfTable.placeAt(this.membersOfTableContainer);
        this.membersOfTable.addDefaultRowActionButton("edit", lang.hitch(this, this.editEntry, "ref_performer"));
        this.membersOfTable.addDefaultRowActionButton("delete", lang.hitch(this, this.deleteMembersOfEntry));
        this.membersOfTable.setRowClick(lang.hitch(this, this._onMembersOfTableRowClick))

        this.membersOfTable.setCyAttribute("PerformerCardsMemberOf");

        this.updateMembersOfTable();
    }
    private updateMembersOfTable() {

        this.membersOfTable.setupFromStore(this.membersOfStore);
        this.pageStack.setPageLabel("membersOf", `${this.translate("AddressWizard_Performer_MembersOf")} (${this.membersOfStore.getCurrentCompleteCount()})`)
    }

    private _onMemberInTableRowClick(row: StaticTable.StaticTableRow<Types.EnsembleMember>, rowGroup: StaticTable.StaticTableRowGroup<Types.EnsembleMember>) {
        let address = rowGroup.entity.ref_ensemble.ref_address;

        this.callEntityView(address.ID, address.gDisp_1, address.Classname)
    }
    private _onMembersOfTableRowClick(row: StaticTable.StaticTableRow<Types.EnsembleMember>, rowGroup: StaticTable.StaticTableRowGroup<Types.EnsembleMember>) {
        let address = rowGroup.entity.ref_performer.ref_address;

        this.callEntityView(address.ID, address.gDisp_1, address.Classname)
    }
    private callEntityView(id: string, heading: string, className: string) {

        EntityHelpers.openEntityView({
            entityClass: className,
            entityId: id,
            label: heading,
            tabContainerName: this.getTabContainerName()
        });

    }

    private _onChangeInterpretHintbox(value) {

        this.addNewEntry(value, "ref_performer");
        this.addInterpretHintbox.setValue(null, true);
    }

    private _onChangeEnsembleHintbox(value) {

        this.addNewEntry(value, "ref_ensemble");
        this.addEnsembleHintbox.setValue(null, true);
    }

    private editEntry(refAttr: "ref_performer" | "ref_ensemble", rowGr: StaticTable.StaticTableRowGroup<Types.EnsembleMember>) {
        let entity = rowGr.entity;
        let title;
        if (entity.isNew) {
            title = `${entity[refAttr].gDisp_1}`;
        } else {
            title = `${entity.ref_performer.gDisp_1} <> ${entity.ref_ensemble.gDisp_1}`;
        }

        let editor = new EnsembleMemberEditor();
        editor.setup(entity, title);
        editor.on("okClicked", lang.hitch(this, this.onEditorOkClicked, entity))
    }

    private onEditorOkClicked(entry: Types.EnsembleMember, changedData: Object) {

        for (let p in changedData) {
            switch (p) {
                case "is_active":
                case "date_start":
                case "date_end":
                    entry[p] = changedData[p];
                default:
                    console.warn(`PerformerCard: Ensemble-Editor property (${p}) not implemented!`)
            }
        }

        switch (this.addressType) {
            case "person":
                this.memberInStore.changeEntry(entry);
                this.updateMemberInTable();
                break;
            case "company":
                this.membersOfStore.changeEntry(entry);
                this.updateMembersOfTable();
                break;
        }
        this.emitDataChanged();
    }
    private deleteMembersOfEntry(rowGr: StaticTable.StaticTableRowGroup<Types.EnsembleMember>) {

        let entry = rowGr.entity;
        this.membersOfStore.deleteEntry(entry);

        this.updateMembersOfTable();
        this.emitDataChanged();
    }

    private deleteMemberInEntry(rowGr: StaticTable.StaticTableRowGroup<Types.EnsembleMember>) {

        let entry = rowGr.entity;
        this.memberInStore.deleteEntry(entry);

        this.updateMemberInTable();
        this.emitDataChanged();
    }

    private addNewEntry(value: Types.Performer, mode: "ref_ensemble" | "ref_performer") {
        if (value == null) return;
        let newId = (Math.random() * 100000).toString();

        let store;
        let updateTable: Function;
        switch (mode) {
            case "ref_ensemble":
                store = this.memberInStore;
                updateTable = lang.hitch(this, this.updateMemberInTable);
                break;
            case "ref_performer":
                store = this.membersOfStore;
                updateTable = lang.hitch(this, this.updateMembersOfTable);
                break;
        }

        let allEntries: Array<Types.EnsembleMember> = store.getCurrentCompleteList();

        let existingEntry = allEntries.filter(x => x[mode].ID === value.ID)[0];
        if (existingEntry) {
            this.showToast({
                title: this.translate(mode == "ref_ensemble" ? "AddressWizard_PerformerSection_Toast_ExistInList" : "AddressWizard_PerformerSection_Toast_Performer_ExistInList"),
                message: `**${value.gDisp_1}**  ${this.translate(mode == "ref_ensemble" ? "AddressWizard_PerformerSection_Toast_ExistInList_details" : "AddressWizard_PerformerSection_Toast_Performer_ExistInList_details")}`,
                messageIcon: "fas fa-info-circle",
                iconClass: "fas fa-info-circle"
            })
            return;
        }

        let fakeEntry: Partial<Types.EnsembleMember> = {
            ID: newId,
            is_active: true,
            [mode]: value,
            isNew: true,
        }

        store.addEntry(fakeEntry, "new");
        updateTable();
        this.emitDataChanged();
    }

    private _onClick_OpenSED(identifierName, evt) {

        this.loadSelectEntityDialog(evt,
            identifierName,
            lang.hitch(this, this._onCallback_Dlg)
        );
    }

    private _onCallback_Dlg(mode: string,
        data,
        items: Array<SelectEntityDialogTypes.Item<Types.Performer>>) {
        // fake-entity falls das zurückgeleiferte Entity aus items[0].entry.data nicht dem entspricht das die liste erwartet

        if (!items || !items.length) return;

        let entryMode;
        switch (mode) {
            case "selectEnsemble":
                entryMode = "ref_ensemble";
                break;
            case "selectPerformer":
                entryMode = "ref_performer";
                break;
        }

        for (let item of items) {
            let entity: Partial<Types.Performer> = item.entry?.data;
            if (!entity) {
                entity = {
                    ID: item.entry.value, gDisp_1: item.entry.label
                };
            }
            this.addNewEntry(entity as any, entryMode);
        }
    }
    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.addEnsembleHintbox.addInlineButton(lang.hitch(this, this._onClick_OpenSED, "selectEnsemble"));
        this.addInterpretHintbox.addInlineButton(lang.hitch(this, this._onClick_OpenSED, "selectPerformer"));
        this.SEDconfigCallfunction = "Webtool_API_AddressWizard__SED_GetConfig";
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(PerformerCard);